import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { alertError, alertSuccess, confirmDelete } from '../../utils/swal';
import AdminLayout from './AdminLayout';
import { EmptyState, LoadingState, MetricCard, PageHeader, TableToolbar } from './AdminUI';

const emptyForm = {
    customerName: '',
    customerTitle: '',
    organization: '',
    quote: '',
    image: '',
    rating: 5,
    featured: false,
    active: true,
    order: 0,
    source: 'manual'
};

const imageUrl = (image) => {
    if (!image) return '';
    if (image.startsWith('http') || image.startsWith('data:')) return image;
    if (image.startsWith('uploads/')) return `${API_BASE.replace(/\/api\/?$/, '')}/${image}`;
    return `${process.env.PUBLIC_URL}/${image.replace(/^\//, '')}`;
};

const renderStars = (rating = 5) => {
    const value = Math.max(1, Math.min(5, Number(rating) || 5));
    return Array.from({ length: 5 }, (_, index) => (
        <i key={index} className={`fa fa-star${index < value ? '' : '-o'}`}></i>
    ));
};

const TestimonialsManagement = () => {
    const { apiCall } = useAuth();
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [formData, setFormData] = useState(emptyForm);
    const [imageFile, setImageFile] = useState(null);
    const [tableSearch, setTableSearch] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchTestimonials();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchTestimonials = async () => {
        try {
            const response = await apiCall('/testimonials/admin');
            const data = await response.json();
            setTestimonials(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            alertError('Error', 'Could not load testimonials');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            const url = editingTestimonial ? `/testimonials/${editingTestimonial.id}` : '/testimonials';
            const method = editingTestimonial ? 'PUT' : 'POST';
            const fd = new FormData();

            Object.entries(formData).forEach(([key, value]) => {
                fd.append(key, String(value ?? ''));
            });
            if (imageFile) {
                fd.append('image', imageFile);
            }

            const response = await apiCall(url, { method, body: fd });

            if (response.ok) {
                await fetchTestimonials();
                closeModal();
                alertSuccess('Saved', 'Testimonial saved successfully.');
            } else {
                const result = await response.json();
                alertError('Error', result.message || 'Error saving testimonial');
            }
        } catch (error) {
            console.error('Error saving testimonial:', error);
            alertError('Error', 'Error saving testimonial');
        }
    };

    const handleEdit = (testimonial) => {
        setEditingTestimonial(testimonial);
        setFormData({
            customerName: testimonial.customerName || '',
            customerTitle: testimonial.customerTitle || '',
            organization: testimonial.organization || '',
            quote: testimonial.quote || '',
            image: testimonial.image || '',
            rating: testimonial.rating || 5,
            featured: Boolean(testimonial.featured),
            active: Boolean(testimonial.active),
            order: testimonial.order || 0,
            source: testimonial.source || 'manual'
        });
        setImageFile(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingTestimonial(null);
        setFormData(emptyForm);
        setImageFile(null);
    };

    const updateFlag = async (testimonial, key) => {
        try {
            const response = await apiCall(`/testimonials/${testimonial.id}`, {
                method: 'PUT',
                body: JSON.stringify({ ...testimonial, [key]: !testimonial[key] })
            });
            if (response.ok) {
                await fetchTestimonials();
            } else {
                alertError('Error', 'Could not update testimonial');
            }
        } catch (error) {
            console.error('Error updating testimonial:', error);
            alertError('Error', 'Could not update testimonial');
        }
    };

    const handleDelete = async (id) => {
        const { isConfirmed } = await confirmDelete({ title: 'Delete testimonial?', text: 'Are you sure? This cannot be undone.' });
        if (!isConfirmed) return;

        try {
            const response = await apiCall(`/testimonials/${id}`, { method: 'DELETE' });
            if (response.ok) {
                await fetchTestimonials();
                alertSuccess('Deleted', 'Testimonial deleted.');
            } else {
                alertError('Error', 'Error deleting testimonial');
            }
        } catch (error) {
            console.error('Error deleting testimonial:', error);
            alertError('Error', 'Error deleting testimonial');
        }
    };

    const filteredTestimonials = useMemo(() => {
        const query = tableSearch.trim().toLowerCase();
        if (!query) return testimonials;

        return testimonials.filter((testimonial) => [
            testimonial.customerName,
            testimonial.customerTitle,
            testimonial.organization,
            testimonial.quote,
            testimonial.active ? 'published' : 'draft',
            testimonial.featured ? 'featured' : ''
        ].some((value) => String(value || '').toLowerCase().includes(query)));
    }, [testimonials, tableSearch]);

    const stats = {
        total: testimonials.length,
        published: testimonials.filter((item) => item.active).length,
        drafts: testimonials.filter((item) => !item.active).length,
        featured: testimonials.filter((item) => item.featured).length
    };

    if (loading) {
        return (
            <AdminLayout>
                <LoadingState label="Loading testimonials..." />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-page">
                <PageHeader
                    title="What Clients Say"
                    subtitle="Manage customer testimonials"
                    action={
                        <button type="button" onClick={() => setShowModal(true)} className="btn btn-primary">
                            <i className="fa fa-plus"></i>
                            Add Testimonial
                        </button>
                    }
                />

                <div className="analytics-metrics-grid">
                    <MetricCard label="Total" value={stats.total} icon="fa fa-list" accent="#62B6E8" detail="All testimonials" />
                    <MetricCard label="Published" value={stats.published} icon="fa fa-eye" accent="#10b981" detail="Visible on site" />
                    <MetricCard label="Drafts" value={stats.drafts} icon="fa fa-pencil" accent="#f59e0b" detail="Hidden from site" />
                    <MetricCard label="Featured" value={stats.featured} icon="fa fa-star" accent="#2F7FAE" detail="Priority quotes" />
                </div>

                <div className="card admin-data-card">
                    <TableToolbar
                        search={tableSearch}
                        onSearchChange={setTableSearch}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={setRowsPerPage}
                        placeholder="Search testimonials..."
                    />
                    {filteredTestimonials.length === 0 ? (
                        <EmptyState icon="fa fa-quote-left" title="No testimonials yet" text="Add customer quotes to build your testimonial dashboard." />
                    ) : (
                        <div className="admin-testimonial-grid">
                            {filteredTestimonials.slice(0, rowsPerPage).map((testimonial, index) => (
                                <article
                                    className={`admin-testimonial-card ${index % 3 === 1 ? 'is-offset' : ''}`}
                                    key={testimonial.id}
                                >
                                    <div className="admin-testimonial-avatar">
                                        {testimonial.image ? (
                                            <img src={imageUrl(testimonial.image)} alt={testimonial.customerName} />
                                        ) : (
                                            <span>{(testimonial.customerName || '?').charAt(0)}</span>
                                        )}
                                    </div>

                                    <div className="admin-testimonial-card-body">
                                        <div className="admin-testimonial-stars">
                                            {renderStars(testimonial.rating)}
                                        </div>
                                        <h3>{testimonial.customerName}</h3>
                                        <p className="admin-testimonial-meta">
                                            {[testimonial.customerTitle, testimonial.organization].filter(Boolean).join(', ') || 'Client'}
                                        </p>
                                        <p className="admin-testimonial-quote">“{testimonial.quote}”</p>
                                    </div>

                                    <div className="admin-testimonial-badges">
                                        <button
                                            type="button"
                                            onClick={() => updateFlag(testimonial, 'active')}
                                            className={`admin-testimonial-pill ${testimonial.active ? 'is-published' : 'is-draft'}`}
                                        >
                                            {testimonial.active ? 'Published' : 'Draft'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateFlag(testimonial, 'featured')}
                                            className={`admin-testimonial-pill ${testimonial.featured ? 'is-featured' : ''}`}
                                        >
                                            <i className={`fa fa-star${testimonial.featured ? '' : '-o'}`}></i>
                                            Featured
                                        </button>
                                    </div>

                                    <div className="admin-testimonial-actions">
                                        <button type="button" onClick={() => handleEdit(testimonial)} className="btn btn-primary" title="Edit">
                                            <i className="fa fa-edit"></i>
                                            Edit
                                        </button>
                                        <button type="button" onClick={() => handleDelete(testimonial.id)} className="btn btn-danger" title="Delete">
                                            <i className="fa fa-trash"></i>
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>

                {showModal && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                        <div style={{ background: '#fff', borderRadius: '0', maxWidth: '760px', width: '90%', maxHeight: '90vh', overflowY: 'auto', padding: '32px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                                    {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
                                </h2>
                                <button type="button" onClick={closeModal} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#718096' }}>
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                                    <Field label="Customer Name *">
                                        <input type="text" value={formData.customerName} onChange={(e) => setFormData({ ...formData, customerName: e.target.value })} required style={inputStyle} />
                                    </Field>
                                    <Field label="Customer Title">
                                        <input type="text" value={formData.customerTitle} onChange={(e) => setFormData({ ...formData, customerTitle: e.target.value })} style={inputStyle} />
                                    </Field>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '16px', marginBottom: '16px' }}>
                                    <Field label="Organization">
                                        <input type="text" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })} style={inputStyle} />
                                    </Field>
                                    <Field label="Display Order">
                                        <input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value, 10) || 0 })} style={inputStyle} />
                                    </Field>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <Field label="Quote *">
                                        <textarea value={formData.quote} onChange={(e) => setFormData({ ...formData, quote: e.target.value })} required rows={5} style={{ ...inputStyle, resize: 'vertical' }} />
                                    </Field>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <Field label="Customer Photo">
                                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <div style={{ width: '90px', height: '90px', border: '2px solid #e2e8f0', borderRadius: '50%', overflow: 'hidden', background: '#f7fafc' }}>
                                                {(imageFile || formData.image) ? (
                                                    <img src={imageFile ? URL.createObjectURL(imageFile) : imageUrl(formData.image)} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '12px' }}>No image</div>
                                                )}
                                            </div>
                                            <input type="file" accept=".jpg,.jpeg,.png,.gif,.webp" onChange={(e) => setImageFile(e.target.files?.[0] || null)} style={{ ...inputStyle, flex: '1', minWidth: '220px' }} />
                                        </div>
                                    </Field>
                                </div>

                                <div style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                                    <label style={checkboxLabelStyle}>
                                        <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} style={checkboxStyle} />
                                        Published
                                    </label>
                                    <label style={checkboxLabelStyle}>
                                        <input type="checkbox" checked={formData.featured} onChange={(e) => setFormData({ ...formData, featured: e.target.checked })} style={checkboxStyle} />
                                        Featured on homepage
                                    </label>
                                </div>

                                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                    <button type="button" onClick={closeModal} style={secondaryButtonStyle}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" style={{ padding: '12px 24px' }}>
                                        {editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

const Field = ({ label, children }) => (
    <label style={{ display: 'block', fontWeight: '500', color: '#2d3748' }}>
        <span style={{ display: 'block', marginBottom: '6px' }}>{label}</span>
        {children}
    </label>
);

const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '2px solid #e2e8f0',
    borderRadius: '0',
    fontSize: '14px'
};

const checkboxLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    fontWeight: '500',
    color: '#2d3748'
};

const checkboxStyle = {
    width: '18px',
    height: '18px'
};

const secondaryButtonStyle = {
    padding: '12px 24px',
    background: '#e2e8f0',
    color: '#4a5568',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    fontWeight: '500'
};

export default TestimonialsManagement;
